import { cn } from '@/lib/utils';

export type ReportTab = 'report' | 'review';

interface ReportTabBarProps {
  activeTab: ReportTab;
  hasInReviewIndicator: boolean;
  onChange: (tab: ReportTab) => void;
}

const TAB_LABELS: Record<ReportTab, string> = { report: 'Gate Report', review: 'Review' };
const TABS: ReportTab[] = ['report', 'review'];

/**
 * Tab bar for Job Detail page — Gate Report / Review.
 * Visible only to reviewer/admin (caller is responsible for the guard).
 * WCAG: role="tablist", role="tab", aria-selected, Arrow key navigation.
 */
export function ReportTabBar({ activeTab, hasInReviewIndicator, onChange }: ReportTabBarProps) {
  function handleKeyDown(e: React.KeyboardEvent, tab: ReportTab) {
    if (e.key === 'ArrowRight') onChange('review');
    if (e.key === 'ArrowLeft') onChange('report');
    if ((e.key === 'Enter' || e.key === ' ') && activeTab !== tab) {
      e.preventDefault();
      onChange(tab);
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Job detail sections"
      className="flex border-b border-border gap-1"
    >
      {TABS.map((tab) => {
        const isActive = activeTab === tab;
        return (
          <button
            key={tab}
            role="tab"
            aria-selected={isActive}
            id={`tab-${tab}`}
            onClick={() => { onChange(tab); }}
            onKeyDown={(e) => { handleKeyDown(e, tab); }}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium',
              'border-b-2 -mb-px transition-colors rounded-t',
              'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              isActive
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {TAB_LABELS[tab]}
            {tab === 'review' && hasInReviewIndicator && (
              <span
                className="h-2 w-2 rounded-full bg-orange-500"
                aria-label="Review in progress"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
